/**
 * NPM registry version checker for self-update support.
 *
 * - checkForUpdate(): non-blocking check against npm registry
 * - runSelfUpdate(): detects install method and runs the appropriate update command
 */

import { request } from "undici";
import { execFile } from "node:child_process";
import { readFileSync, existsSync } from "node:fs";
import { join, dirname } from "node:path";
import { fileURLToPath } from "node:url";
import { logger } from "./logger.js";
import { readOrgConfig } from "../setup/org-config.js";

const PACKAGE_NAME = "powerautomate-mcp";
const REGISTRY_URL = `https://registry.npmjs.org/${PACKAGE_NAME}/latest`;
const CHECK_TIMEOUT_MS = 5_000;

/**
 * Read the installed version from package.json.
 * Walks up from the executing script's directory until it finds package.json,
 * which handles both unbundled (dist/utils/) and bundled (dist/) layouts.
 */
export function getInstalledVersion(): string {
  try {
    let dir = dirname(fileURLToPath(import.meta.url));
    // Walk up at most 5 levels to find package.json
    for (let i = 0; i < 5; i++) {
      const candidate = join(dir, "package.json");
      if (existsSync(candidate)) {
        const pkg = JSON.parse(readFileSync(candidate, "utf-8")) as {
          name?: unknown;
          version?: unknown;
        };
        if (pkg.name === PACKAGE_NAME && typeof pkg.version === "string") {
          return pkg.version;
        }
      }
      const parent = dirname(dir);
      if (parent === dir) break; // filesystem root
      dir = parent;
    }
    return "0.0.0";
  } catch {
    return "0.0.0";
  }
}

export interface UpdateInfo {
  currentVersion: string;
  latestVersion: string;
  updateAvailable: boolean;
}

/**
 * Check the npm registry for the latest published version.
 * Returns null on any failure (network, timeout, parse error) — never throws.
 */
export async function checkForUpdate(): Promise<UpdateInfo | null> {
  const currentVersion = getInstalledVersion();
  try {
    const { statusCode, body } = await request(REGISTRY_URL, {
      method: "GET",
      headers: { accept: "application/json" },
      signal: AbortSignal.timeout(CHECK_TIMEOUT_MS),
    });

    if (statusCode !== 200) {
      await body.text(); // drain
      return null;
    }

    const data = (await body.json()) as { version?: string };
    const latestVersion = data.version;
    if (!latestVersion) return null;

    return {
      currentVersion,
      latestVersion,
      updateAvailable: compareVersions(latestVersion, currentVersion) > 0,
    };
  } catch (err) {
    logger.debug({ err }, "Update check failed (non-fatal)");
    return null;
  }
}

/**
 * Compare two semver strings. Returns >0 if a > b, <0 if a < b, 0 if equal.
 */
function compareVersions(a: string, b: string): number {
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < 3; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * Detect how the package was installed and return the appropriate update command.
 */
function getUpdateCommand(): { cmd: string; args: string[] } {
  // Check if running via npx (common for MCP servers)
  const npmExecPath = process.env["npm_execpath"] ?? "";
  const npmCommand = process.env["npm_command"] ?? "";

  if (npmExecPath.includes("npx") || npmCommand === "exec") {
    // npx caches, so user just needs to run with @latest
    return { cmd: "npx", args: [`${PACKAGE_NAME}@latest`] };
  }

  // Check for yarn
  if (npmExecPath.includes("yarn")) {
    return { cmd: "yarn", args: ["global", "add", PACKAGE_NAME] };
  }

  // Check for pnpm
  if (npmExecPath.includes("pnpm")) {
    return { cmd: "pnpm", args: ["add", "-g", PACKAGE_NAME] };
  }

  // Default: npm global install
  return { cmd: "npm", args: ["install", "-g", PACKAGE_NAME] };
}

/**
 * Run the self-update process.
 * Prints progress to stderr and exits when done.
 */
export async function runSelfUpdate(): Promise<void> {
  const info = await checkForUpdate();

  if (info === null) {
    console.error("Could not reach npm registry. Check your internet connection.");
    process.exit(1);
  }

  if (!info.updateAvailable) {
    console.error(`Already up to date (v${info.currentVersion}).`);
    process.exit(0);
  }

  console.error(`Updating ${PACKAGE_NAME}: v${info.currentVersion} → v${info.latestVersion}`);

  const { cmd, args } = getUpdateCommand();
  console.error(`Running: ${cmd} ${args.join(" ")}`);

  return new Promise((_resolve) => {
    const child = execFile(cmd, args, { timeout: 120_000 }, (error, stdout, stderr) => {
      if (error) {
        console.error(`\nUpdate failed: ${error.message}`);
        if (stderr) console.error(stderr);
        console.error(`\nYou can update manually: npm install -g ${PACKAGE_NAME}`);
        process.exit(1);
      }
      if (stdout) console.error(stdout.trim());
      console.error(`\nUpdated to v${info.latestVersion} successfully.`);
      process.exit(0);
    });

    // Stream output in real-time
    child.stdout?.pipe(process.stderr);
    child.stderr?.pipe(process.stderr);
  });
}

/**
 * Non-blocking startup check — prints a notice to stderr if an update is
 * available, and records it for the once-per-session in-chat notice.
 * Fires and forgets; never blocks the server startup.
 */
export function backgroundUpdateCheck(): void {
  checkForUpdate()
    .then((info) => {
      recordUpdateInfo(info);
      if (info?.updateAvailable) {
        console.error(
          `\n  Update available: v${info.currentVersion} → v${info.latestVersion}\n` +
          `  Run: powerautomate-mcp --update\n`
        );
      }
    })
    .catch(() => {
      // Silently ignore — update check is best-effort
    });
}

// ---------------------------------------------------------------------------
// In-chat update notice — once per session
// ---------------------------------------------------------------------------
// The stderr notice above lands in the AI client's log file, which the person
// chatting never reads. This half surfaces the same fact where the user
// actually is: the first successful tool result after the check resolves
// carries one appended line, then the session stays clean. The registry is
// never touched on the tool path — the notice only ever uses an answer the
// boot-time check already put in hand.

let sessionUpdateInfo: UpdateInfo | null = null;
let noticeDelivered = false;
let orgManagedMemo: boolean | null = null;

/** Store a resolved check for the in-chat notice. Called by backgroundUpdateCheck. */
export function recordUpdateInfo(info: UpdateInfo | null): void {
  if (info?.updateAvailable) {
    sessionUpdateInfo = info;
  }
}

/**
 * Machines provisioned by IT (org.json present) don't get nagged: users there
 * usually can't self-update a global install, and the MDM guide already puts
 * updates in IT's hands. PA_MCP_UPDATE_NOTICE=1 re-enables for orgs that want
 * users aware; =0 disables everywhere.
 */
function isOrgManaged(): boolean {
  if (orgManagedMemo === null) {
    try {
      // Lazy require keeps CLI one-shots from paying the fs probes.
      orgManagedMemo = readOrgConfig() !== null;
    } catch {
      orgManagedMemo = false;
    }
  }
  return orgManagedMemo;
}

/**
 * The once-per-session notice line, or null (no update known yet, already
 * delivered, or suppressed). `orgManagedOverride` is for tests; production
 * callers pass nothing.
 */
export function consumeUpdateNotice(orgManagedOverride?: boolean): string | null {
  if (noticeDelivered || !sessionUpdateInfo) {
    return null;
  }

  const flag = process.env["PA_MCP_UPDATE_NOTICE"]?.trim().toLowerCase();
  if (flag === "0" || flag === "false") {
    noticeDelivered = true;
    return null;
  }
  const orgManaged = orgManagedOverride ?? isOrgManaged();
  if (orgManaged && flag !== "1" && flag !== "true") {
    noticeDelivered = true;
    return null;
  }

  noticeDelivered = true;
  const { cmd, args } = getUpdateCommand();
  const command =
    cmd === "npm"
      ? `npm install -g ${PACKAGE_NAME}@latest`
      : `${cmd} ${args.join(" ")}`;
  return (
    `ℹ️ ${PACKAGE_NAME} v${sessionUpdateInfo.latestVersion} is available ` +
    `(you're running v${sessionUpdateInfo.currentVersion}). ` +
    `To update: quit your AI apps, then run: ${command}`
  );
}

/** Test hook: clears the session-notice state. */
export function resetUpdateNoticeForTests(): void {
  sessionUpdateInfo = null;
  noticeDelivered = false;
  orgManagedMemo = null;
}
